/**
 * BTK - "More details" popup for a unit's Height / Depth options
 * ------------------------------------------------------------------
 * ONE popup for every surface that sells a unit by size:
 *   - the planner's Add Units and Edit (Change Size) popups
 *     (buttons drawn by btk-kitchen-planner-ui-variant-options.js),
 *   - the kitchen-units listing cards and the unit product page
 *     (their existing "?" badges, wired by the delegated listener below).
 *
 * It is GENERAL information about heights and depths, so it deliberately
 * doesn't name the unit that was clicked. That unit's category only decides
 * WHICH range and explanation apply (tall vs wall, 570 vs 330 standard depth).
 *
 * It shows two things:
 *   1. The REAL SIZES of the unit TYPE's general range, from window.BTKUnitDims:
 *      wall / tall heights as Low / Medium / High in millimetres (mirroring the
 *      planner) with floor-to-top; top box heights; the standard + reduced
 *      depths. NOTHING is read from the product's own variants or options -
 *      a wall unit sold in one height still gets all three.
 *   2. An EXPLANATION the team writes in Shopify admin: Content → Metaobjects
 *      → "Unit Option Help" (type unit_option_help), emitted onto the page as
 *      window.BTKOptionHelpData by snippets/btk-option-help.liquid. Looked up
 *      most-specific first: "height:tall" → "height".
 *
 * Deliberately a PLAIN script (window.BTKOptionHelp), like BTKUnitDims: the
 * product pages are not ES-module pages, and the planner reads it at call
 * time without joining its versioned module graph.
 *
 * API
 *   BTKOptionHelp.open({ axis, categoryHandle, groupHandle, plinthMm, opener })
 *   BTKOptionHelp.close()
 */
(function () {
  'use strict';
  if (window.BTKOptionHelp) return;   // included twice on one page - keep the first

  var AXES = { height: true, depth: true };
  var overlay = null;
  var lastOpener = null;

  function dims() { return window.BTKUnitDims || null; }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Merchant text: a blank line starts a new paragraph, a single newline is
  // a line break. Escaped first - it is typed in admin, never trusted HTML.
  function paragraphs(text) {
    var t = String(text || '').replace(/\r\n/g, '\n').trim();
    if (!t) return '';
    return t.split(/\n{2,}/).map(function (p) {
      return '<p>' + esc(p).replace(/\n/g, '<br>') + '</p>';
    }).join('');
  }

  // The help entry for an axis in a family: "height:tall", then "height".
  function helpFor(axis, family) {
    var data = window.BTKOptionHelpData;
    var list = (data && Array.isArray(data.entries)) ? data.entries : [];
    var keys = family ? [axis + ':' + family, axis] : [axis];
    for (var k = 0; k < keys.length; k++) {
      for (var i = 0; i < list.length; i++) {
        if (String(list[i].key || '').trim().toLowerCase() === keys[k]) return list[i];
      }
    }
    return null;
  }

  // One row per size in the unit TYPE's general range, in the order a customer
  // expects. Heights: the family's range (heightRange). Depths: the family's
  // standard + reduced depths (depthRange), each tagged.
  function sizeRows(axis, hFamily, dRange, plinthMm) {
    var D = dims();
    if (!D) return { rows: [], showTop: false };
    var list = axis === 'height'
      ? D.heightRange(hFamily)
      : (dRange ? [dRange.standard].concat(dRange.reduced) : []);
    list = D.sortValues(axis, list.map(String));
    var rows = list.map(function (v) {
      var asNumber = D.numeric(v);
      var mm = asNumber != null ? asNumber : (axis === 'height' ? D.heightMm(v, hFamily) : null);
      return {
        // A numeric value IS its size ("570" -> "570mm"); a named one
        // ("Medium") keeps its name and gets its size in its own column.
        label: asNumber != null ? asNumber + 'mm' : String(v),
        named: asNumber == null,
        // Depth: the family's own standard (330 wall-hung, 570 floor-standing).
        // Height: Medium, the listing's Standard column.
        standard: axis === 'depth'
          ? (dRange != null && asNumber === dRange.standard)
          : D.isStandardValue(v),
        mm: mm,
        // Floor to the top of the unit: a tall unit's carcase + plinth, a
        // wall unit's mount line - both mirror the planner, so the popup
        // matches where the plan puts the unit.
        topMm: axis === 'height' ? D.topFromFloorMm(v, hFamily, plinthMm) : null
      };
    });
    // The column only when at least one size has a floor-to-top figure.
    return { rows: rows, showTop: rows.some(function (r) { return r.topMm != null; }) };
  }

  function tableHTML(axis, sized) {
    var rows = sized.rows;
    if (!rows.length) return '';
    // The size column only earns its place when a value is NAMED - for
    // "570" it would just repeat the value.
    var sizeCol = rows.some(function (r) { return r.named && r.mm != null; });
    var head =
      '<tr><th scope="col">' + (axis === 'height' ? 'Height' : 'Depth') + '</th>' +
      (sizeCol ? '<th scope="col">Carcase height</th>' : '') +
      (sized.showTop ? '<th scope="col">Floor to top</th>' : '') +
      '</tr>';
    var body = rows.map(function (r) {
      var tag = r.standard
        ? ' <span class="btk-oh-tag">Standard</span>'
        : (axis === 'depth' ? ' <span class="btk-oh-tag btk-oh-tag--alt">Reduced</span>' : '');
      return '<tr>' +
        '<td>' + esc(r.label) + tag + '</td>' +
        (sizeCol ? '<td>' + (r.mm != null ? r.mm + 'mm' : '-') + '</td>' : '') +
        (sized.showTop ? '<td>' + (r.topMm != null ? r.topMm + 'mm' : '-') + '</td>' : '') +
        '</tr>';
    }).join('');
    return '<table class="btk-oh-sizes"><thead>' + head + '</thead><tbody>' + body + '</tbody></table>';
  }

  // The right-hand image: the one uploaded on the Unit Option Help entry, or a
  // placeholder marking the spot until it is. The placeholder is deliberately
  // plain - it shows the team where the image goes, and says so.
  function mediaHTML(help, axis) {
    if (help && help.imageUrl) {
      return '<figure class="btk-oh-media">' +
               '<img src="' + esc(help.imageUrl) + '" alt="' + esc(help.title || '') + '" loading="lazy">' +
             '</figure>';
    }
    return '<figure class="btk-oh-media btk-oh-media--placeholder" aria-hidden="true">' +
             '<svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
               '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>' +
             '</svg>' +
             '<figcaption>Image placeholder</figcaption>' +
           '</figure>';
  }

  function onKey(e) {
    if (e.key === 'Escape' && overlay) {
      // Only this popup - not the planner popup it may be sitting on.
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  }

  function close() {
    if (!overlay) return;
    overlay.remove();
    overlay = null;
    document.removeEventListener('keydown', onKey, true);
    if (lastOpener && typeof lastOpener.focus === 'function') {
      try { lastOpener.focus(); } catch (_) {}
    }
    lastOpener = null;
  }

  function open(opts) {
    opts = opts || {};
    var axis = String(opts.axis || '').toLowerCase();
    if (!AXES[axis]) return false;
    close();

    var D = dims();
    // The unit TYPE's families - the only thing about the unit this reads.
    var hFamily = D ? D.heightFamily(opts.categoryHandle, opts.groupHandle) : null;
    var dFamily = D ? D.depthFamily(opts.categoryHandle, opts.groupHandle) : null;
    var plinth = Number(opts.plinthMm) || 0;
    // Explanation keyed by family: "height:wall", "depth:wall", then "height".
    var help = helpFor(axis, axis === 'height' ? hFamily : dFamily);
    var sized = sizeRows(axis, hFamily, D ? D.depthRange(dFamily) : null, plinth);
    var title = (help && help.title) || (axis === 'height' ? 'Unit heights' : 'Unit depths');
    var titleId = 'btk-oh-title-' + Date.now().toString(36);

    overlay = document.createElement('div');
    overlay.className = 'btk-oh';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', titleId);
    overlay.innerHTML =
      '<div class="btk-oh-backdrop" data-btk-oh-close></div>' +
      '<div class="btk-oh-panel">' +
        '<header class="btk-oh-head">' +
          '<h2 id="' + titleId + '">' + esc(title) + '</h2>' +
          '<button type="button" class="btk-oh-x" data-btk-oh-close aria-label="Close">' +
            '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
          '</button>' +
        '</header>' +
        // Sizes and explanation on the left; the image on the right, running
        // the full height of both. Stacks on a narrow screen (see the CSS).
        '<div class="btk-oh-content">' +
          '<div class="btk-oh-main">' +
            tableHTML(axis, sized) +
            (help && help.description ? '<div class="btk-oh-body">' + paragraphs(help.description) + '</div>' : '') +
          '</div>' +
          mediaHTML(help, axis) +
        '</div>' +
        '<footer class="btk-oh-foot">' +
          '<button type="button" class="btk-oh-ok" data-btk-oh-close>Got it</button>' +
        '</footer>' +
      '</div>';

    overlay.addEventListener('click', function (e) {
      if (e.target.closest('[data-btk-oh-close]')) close();
    });
    document.addEventListener('keydown', onKey, true);
    document.body.appendChild(overlay);
    lastOpener = opts.opener || document.activeElement;
    var ok = overlay.querySelector('.btk-oh-ok');
    if (ok) { try { ok.focus(); } catch (_) {} }
    return true;
  }

  // ---- Product pages: the existing "?" badges ----------------------------
  // A badge carries data-btk-option-help="height|depth"; the unit TYPE's
  // context (category / group / plinth) sits on the nearest
  // [data-btk-oh-context]. The product's own option pills are NOT read - the
  // popup is general information for the unit type.
  function openFromBadge(badge) {
    var axis = badge.getAttribute('data-btk-option-help');
    var ctx = badge.closest('[data-btk-oh-context]');
    return open({
      axis: axis,
      categoryHandle: ctx && ctx.getAttribute('data-btk-oh-category'),
      groupHandle:    ctx && ctx.getAttribute('data-btk-oh-group'),
      plinthMm:       ctx && ctx.getAttribute('data-btk-oh-plinth'),
      opener: badge
    });
  }

  document.addEventListener('click', function (e) {
    var badge = e.target.closest && e.target.closest('[data-btk-option-help]');
    if (!badge) return;
    e.preventDefault();
    e.stopPropagation();   // a card may treat any click inside it as "open the product"
    openFromBadge(badge);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    var badge = e.target.closest && e.target.closest('[data-btk-option-help]');
    if (!badge) return;
    e.preventDefault();
    openFromBadge(badge);
  });

  window.BTKOptionHelp = { open: open, close: close, helpFor: helpFor };
})();
